import { useBackendApi } from './backendApi';
import { useEventsPublisher } from 'event-bus';
import { PERSON_UPDATED } from './peopleService';
import { ExternalLink, Person } from './Types';
import { ContactMatch } from './contactsSearchService';

export interface CreateContactLinkRequest {
  externalContactId: string;
  connectionProfileId: number;
}


export enum AutomaticLinkResultType {
  NEW_EXTERNAL_CONTACT_CREATED = 'new_external_contact_created',
  EXISTING_EXTERNAL_CONTACT_LINKED = 'existing_external_contact_found',
  CANNOT_LINK_AUTOMATICALLY = 'cannot_link_automatically',
  ALREADY_LINKED = 'already_linked'
}

export interface ExternalContact {
  id: string
  name: {
    firstName: string
    lastName: string
  },
  emails: string[],
  _externalProfileUrl: string,
}

export interface SearchResult {
  contact: ExternalContact,
  matches: ContactMatch[]
}

export interface LinkAutomaticallyResponse {
  type: AutomaticLinkResultType;
  isLinkCreated: boolean;
  contactLinkId: number | null;
  externalContact?: ExternalContact;
  searchResults: SearchResult[];
  externalLink?: ExternalLink;
}

export interface ContactLink {
  id: number;
  contactId: number;
  externalContactId: string;
  connectionProfileId: number;
}

export enum ContactSyncDirection {
  EXTERNAL_TO_LOCAL = 'external_to_local',
  LOCAL_TO_EXTERNAL = 'local_to_external',
}

export function useContactLinkService() {
  const nekstApi = useBackendApi();
  const eventsPublisher = useEventsPublisher();

  const linkContact = async (contactId: number, request: CreateContactLinkRequest): Promise<Person> => {
    const response = await nekstApi.post(`/contacts/${contactId}/externalcontactslinks`, request) as Person;

    eventsPublisher.publish(PERSON_UPDATED, {
      id: response.id,
      data: response
    });
    return response;
  };

  const unlinkContact = async (contactId: number, linkId: number): Promise<Person> => {
    const response = await nekstApi.delete(`/contacts/${contactId}/externalcontactslinks/${linkId}`) as Person;
    eventsPublisher.publish(PERSON_UPDATED, {
      id: response.id,
      data: response
    });
    return response;
  };

  const linkContactAutomatically = async (
    contactId: number,
    connectionProfileId: number
  ): Promise<LinkAutomaticallyResponse> => {
    return await nekstApi.post(`/contacts/${contactId}/externalcontactslinks/auto`, {
      connectionProfileId,
    }) as LinkAutomaticallyResponse;
  };

  const batchLinkAutomatically = async (
    contactsIds: number[],
    connectionProfileId: number,
  ): Promise<Record<number, LinkAutomaticallyResponse>> => {
    return await nekstApi.post('/contacts/externalcontactslinks/batch', {
      contactsIds,
      connectionProfileId,
    });
  };

  const triggerSync = async (contactId: number, linkId: number) => {
    await nekstApi.post(`/contacts/${contactId}/externalcontactslinks/${linkId}/sync`, {});

    eventsPublisher.publish(PERSON_UPDATED, {
      id: contactId,
    });
  };

  const updateExternalContact = async (contactId: number, linkId: number) => {
    await nekstApi.post(`/contacts/${contactId}/externalcontactslinks/${linkId}/updateexternal`, {});

    eventsPublisher.publish(PERSON_UPDATED, {
      id: contactId,
    });
  };

  const updateNekstContact = async (contactId: number, linkId: number) => {
    await nekstApi.post(`/contacts/${contactId}/externalcontactslinks/${linkId}/updatelocal`, {});

    eventsPublisher.publish(PERSON_UPDATED, {
      id: contactId,
    });
  };

  return {
    linkContact,
    unlinkContact,
    linkContactAutomatically,
    batchLinkAutomatically,
    triggerSync,
    updateExternalContact,
    updateNekstContact,
  };
}
